import React, { MouseEvent, useLayoutEffect, useState } from "react";
import {
    Box,
    Divider,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Typography,
} from "@mui/material";
import { Styled } from "./styles";
import { useSidebar } from "@dippi/forms";
import SectionIcon from "./SectionIcon";
import { useSectionObserver } from "./hooks";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";

interface SidebarProps {
    title: string;
}

const Sidebar: React.FC<SidebarProps> = ({ title }) => {
    const { sections } = useSidebar();
    const baseSections = sections.filter(section => section.type === "base");
    const marketingSections = sections.filter(section => section.type === "marketing");

    const [clickedId, setClickedId] = useState("");
    const [currentSection, setCurrentSection] = useState("");

    const handleClick = (id: string, e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setClickedId(id);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    };

    useSectionObserver(setCurrentSection, setClickedId);

    const getIsActive = (id: string) => {
        return clickedId === id || (currentSection === id && clickedId === "");
    };

    useLayoutEffect(() => {
        document.documentElement.style.scrollPaddingTop = "120px";

        return () => {
            document.documentElement.style.scrollPaddingTop = "unset";
        };
    }, []);

    const [open, setOpen] = useState(true);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    return (
        <Styled.Sidebar variant="permanent" open={open}>
            <Styled.Header isOpen={open}>
                <IconButton
                    onClick={open ? handleDrawerClose : handleDrawerOpen}
                    title={`${open ? "Close" : "Open"}`}
                >
                    {open ? (
                        <Box
                            width="100%"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            padding="8px 0px 8px 4px;"
                        >
                            <Typography
                                fontSize={18}
                                fontWeight={700}
                                maxWidth="200px"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                                title={title}
                            >
                                {title}
                            </Typography>
                            <ChevronLeftIcon />
                        </Box>
                    ) : (
                        <MenuIcon />
                    )}
                </IconButton>
            </Styled.Header>
            <Styled.List disablePadding>
                <ListSubheader>Base information</ListSubheader>
                {baseSections.map(section => {
                    const { id, title } = section;

                    return (
                        <ListItem disablePadding key={id}>
                            <Styled.ListButton
                                onClick={e => handleClick(id, e)}
                                isActive={getIsActive(id)}
                                title={open ? "" : title}
                            >
                                <ListItemIcon>
                                    <SectionIcon id={id} />
                                </ListItemIcon>
                                <ListItemText primary={title} />
                            </Styled.ListButton>
                        </ListItem>
                    );
                })}
                <ListSubheader>Marketing</ListSubheader>
                {!open && <Divider />}
                {marketingSections.map(section => {
                    const { id, title } = section;

                    return (
                        <ListItem disablePadding key={id}>
                            <Styled.ListButton
                                onClick={e => handleClick(id, e)}
                                isActive={getIsActive(id)}
                                title={open ? "" : title}
                            >
                                <ListItemIcon>
                                    <SectionIcon id={id} />
                                </ListItemIcon>
                                <ListItemText primary={title} />
                            </Styled.ListButton>
                        </ListItem>
                    );
                })}
            </Styled.List>
        </Styled.Sidebar>
    );
};

export default Sidebar;
